import { observable, computed, action } from 'mobx';
import { app } from '../App';
import { readFileAsDataUrl } from '../../../common/helpers/file';
import {
    IShippingListUpload,
    IShippingListEntry,
    IShippingListEntryValidation,
    EShippingListEntryStatus,
    EShippingListDataFilter
} from '../../../common/interfaces/shippingList';
import { unformat } from 'accounting';

// Enumerables
export enum EShippingListUploadStatus {
    None,
    VendorSearch,
    WarehouseSearch,
    CSVUpload,
    CSVSubmission
}

export enum EShippingListUploadProgress {
    None,
    Saving,
    Error,
    Success
}

// Store
export class ShippingListUpload {
    // Progress
    @observable public status: EShippingListUploadStatus = EShippingListUploadStatus.None;

    // Vendor
    @observable public selectedVendorCardCode: string | null = null;

    // Warehouse
    @observable public selectedWarehouseCode: string | null = null;

    // CSV file
    @observable public selectedCsvFiles: FileList | null = null;
    @observable public uploadingCsvFile: boolean = false;

    // CSV validation
    @observable public shippingListId: number | null = null;
    @observable public shippingListData: IShippingListEntry[] = [];
    @observable public shippingListDataFilter: EShippingListDataFilter = EShippingListDataFilter.All;
    @observable public shippingListSaveProgress: EShippingListUploadProgress = EShippingListUploadProgress.None;

    // Filtered CSV validation
    @computed
    public get shippingListDataFiltered(): IShippingListEntry[] {
        return this.shippingListDataFilter === EShippingListDataFilter.All
            ? this.shippingListData
            : this.shippingListData.filter(entry => {
                  if (this.shippingListDataFilter === EShippingListDataFilter.QuantityDifferences) {
                      return entry.status === EShippingListEntryStatus.Invalid ? true : false;
                  } else if (this.shippingListDataFilter === EShippingListDataFilter.PriceDifferences) {
                      return entry.status === EShippingListEntryStatus.ValidWithDifferentPrices ||
                          entry.status === EShippingListEntryStatus.DuplicateItemsPriceInvalid
                          ? true
                          : false;
                  }

                  return false;
              });
    }

    // User is after vendor selection step
    @computed
    public get userIsAfterVendorStep(): boolean {
        return (
            this.status !== EShippingListUploadStatus.None &&
            this.status !== EShippingListUploadStatus.VendorSearch &&
            this.selectedVendorCardCode !== null
        );
    }

    // User is after warehouse selection step
    @computed
    public get userIsAfterWarehouseStep(): boolean {
        return (
            this.userIsAfterVendorStep &&
            this.status !== EShippingListUploadStatus.WarehouseSearch &&
            this.selectedWarehouseCode !== null
        );
    }

    // User is on CSV upload step
    @computed
    public get userIsOnCsvUploadStep(): boolean {
        return this.status === EShippingListUploadStatus.CSVUpload;
    }

    // User is on CSV validation step
    @computed
    public get userIsOnCsvValidationStep(): boolean {
        return this.status === EShippingListUploadStatus.CSVSubmission;
    }

    // User is after CSV upload step
    @computed
    public get userIsAfterCsvUploadStep(): boolean {
        return (
            this.userIsAfterVendorStep &&
            this.userIsAfterWarehouseStep &&
            this.selectedCsvFiles &&
            this.selectedCsvFiles.length > 0
        );
    }

    // Check all entries are valid
    @computed
    public get allEntriesAreValid(): boolean {
        if (this.userIsAfterCsvUploadStep === false) {
            return false;
        }

        if (this.uploadingCsvFile) {
            return false;
        }

        if (this.selectedCsvFiles === null) {
            return false;
        }

        const invalidEntries = this.shippingListData.filter(entry => entry.isValid === false);
        if (invalidEntries.length > 0) {
            return false;
        }

        return true;
    }

    // Check if any entry has price mismatch
    @computed
    public get anyEntryHasPriceMismatch(): boolean {
        if (this.userIsAfterCsvUploadStep === false || this.uploadingCsvFile || this.selectedCsvFiles === null) {
            return false;
        }

        const priceMismatchEntries = this.shippingListData.filter(
            entry => entry.status === EShippingListEntryStatus.ValidWithDifferentPrices
        );

        return priceMismatchEntries.length > 0 ? true : false;
    }

    // Check if any entry has duplicate items price mismatch
    @computed
    public get anyEntryHasDuplicateItemsPriceMismatch(): boolean {
        if (this.userIsAfterCsvUploadStep === false || this.uploadingCsvFile || this.selectedCsvFiles === null) {
            return false;
        }

        const duplicateItemsPriceMismatchEntries = this.shippingListData.filter(
            entry => entry.status === EShippingListEntryStatus.DuplicateItemsPriceInvalid
        );

        return duplicateItemsPriceMismatchEntries.length > 0 ? true : false;
    }

    // Change selected vendor card code
    @action
    public changeSelectedVendorCardCode = (cardCode: string | null = null) => {
        this.selectedVendorCardCode = cardCode;

        if (cardCode !== null && this.status === EShippingListUploadStatus.VendorSearch) {
            this.status = EShippingListUploadStatus.WarehouseSearch;
        }
    };

    // Change selected warehouse code
    @action
    public changeSelectedWarehouseCode = (whsCode: string | null = null) => {
        this.selectedWarehouseCode = whsCode;

        if (whsCode !== null && this.status === EShippingListUploadStatus.WarehouseSearch) {
            this.status = EShippingListUploadStatus.CSVUpload;
        }
    };

    // Change picked files for upload
    @action
    public changeSelectedFileList = (fileList: FileList | null = null) => {
        this.selectedCsvFiles = fileList;

        if (fileList && fileList.length > 0 && this.status === EShippingListUploadStatus.CSVUpload) {
            this.status = EShippingListUploadStatus.CSVSubmission;

            this.uploadCsvFile();
        }
    };

    // Send CSV file to the API
    @action
    uploadCsvFile = async (): Promise<boolean> => {
        try {
            if (
                !this.selectedCsvFiles ||
                (typeof this.selectedCsvFiles.length !== 'undefined' && this.selectedCsvFiles.length <= 0)
            ) {
                throw new Error('File is required');
            }

            this.uploadingCsvFile = true;

            const file = await readFileAsDataUrl(this.selectedCsvFiles[0]);

            const service = await app.service('po-upload');
            const validation = (await service.create(
                {
                    uri: file
                },
                {
                    query: {
                        cardcode: this.selectedVendorCardCode,
                        whsCode: this.selectedWarehouseCode
                    }
                }
            )) as IShippingListUpload;

            if (this.uploadingCsvFile) {
                this.shippingListId = validation.id;
                this.shippingListData = validation.data.map(entry => ({
                    itemcode: entry.ItemCode,
                    quantity: unformat(entry.Quantity),
                    price: unformat(entry.Price),
                    poPrice: unformat(entry.POPrice),
                    totalAllocated: unformat(entry.TotalAllocated),
                    isValid: entry.Status > 0,
                    status:
                        entry.Status === 1
                            ? EShippingListEntryStatus.Valid
                            : entry.Status === 2
                              ? EShippingListEntryStatus.ValidWithDifferentPrices
                              : entry.Status === -1
                                ? EShippingListEntryStatus.DuplicateItemsPriceInvalid
                                : EShippingListEntryStatus.Invalid
                }));
            }

            this.uploadingCsvFile = false;

            return true;
        } catch (error) {
            console.error(error);
            return error;
        }
    };

    // Submit validated data
    @action
    public submit = async (): Promise<boolean> => {
        try {
            this.shippingListSaveProgress = EShippingListUploadProgress.Saving;

            if (this.allEntriesAreValid === false) {
                throw new Error('All entries need to be valid');
            }

            const service = await app.service('po-upload-confirm');
            const confirmation = await service.create({ id: this.shippingListId });

            this.shippingListSaveProgress = EShippingListUploadProgress.Success;

            return true;
        } catch (error) {
            this.shippingListSaveProgress = EShippingListUploadProgress.Error;
            console.error(error);
            return error;
        }
    };

    // Begin the process
    @action
    public initialize = () => {
        this.status = EShippingListUploadStatus.VendorSearch;
        this.selectedCsvFiles = null;
        this.uploadingCsvFile = false;
        this.shippingListId = null;
        this.shippingListData = [];
        this.shippingListDataFilter = EShippingListDataFilter.All;
        this.shippingListSaveProgress = EShippingListUploadProgress.None;
    };

    // Go back one step
    @action
    public goStepBack = () => {
        if (this.status !== EShippingListUploadStatus.None && this.status !== EShippingListUploadStatus.VendorSearch) {
            switch (this.status) {
                case EShippingListUploadStatus.WarehouseSearch:
                    this.status = EShippingListUploadStatus.VendorSearch;
                    break;

                case EShippingListUploadStatus.CSVUpload:
                    this.status = EShippingListUploadStatus.WarehouseSearch;
                    break;

                case EShippingListUploadStatus.CSVSubmission:
                    // Only if user is not already saving the file
                    if (this.shippingListSaveProgress !== EShippingListUploadProgress.Saving) {
                        this.status = EShippingListUploadStatus.CSVUpload;
                        this.selectedCsvFiles = null;
                        this.uploadingCsvFile = false;
                        this.shippingListId = null;
                        this.shippingListData = [];
                        this.shippingListDataFilter = EShippingListDataFilter.All;
                    }
                    break;

                default:
                    break;
            }
        }
    };
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./app/web/src/stores/ShippingListUpload.ts